
import { find } from 'lodash';
import { sync } from 'vuex-pathify';

import DemoForm from '@/components/DemoForm.vue';
import MonacoEditor from '@/components/MonacoEditor.vue';
import VuetifyJsonFormsWrapper from '@/components/VuetifyJsonFormsWrapper.vue';
import {
  configureDataValidation,
  configureJsonSchemaValidation,
  configureUISchemaValidation,
  EditorApi,
  getMonacoModelForUri,
} from '@/core/jsonSchemaValidation';
import { Example } from '@/core/types';
import { AppStore } from '@/store/modules/types';
import { JsonFormsRendererRegistryEntry } from '@jsonforms/core';
import { JsonFormsChangeEvent } from '@jsonforms/vue2';
import { defaultStyles, mergeStyles } from '@jsonforms/vue2-vuetify';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

const myStyles = mergeStyles(defaultStyles, {
  control: { root: 'my-control' },
});

export default {
  name: 'ExampleView',
  components: {
    MonacoEditor,
    DemoForm,
    VuetifyJsonFormsWrapper,
  },
  data() {
    return {
      useWebComponentView: false,
      activeTab: 0,
      snackbar: false,
      snackbarText: '',
      snackbarTimeout: 3000,
    };
  },
  computed: {
    app: sync<AppStore>('app/'),
    renderers: sync('app/jsonforms@renderers'),
    allRenderers(): JsonFormsRendererRegistryEntry[] {
      return (this.app.example?.input.renderers ?? []).concat(this.renderers);
    },
    cells: sync('app/jsonforms@cells'),
    config: sync('app/jsonforms@config'),
    validationMode: sync('app/jsonforms@validationMode'),
    ajv: sync('app/jsonforms@ajv'),
    readonly: sync('app/jsonforms@readonly'),
    monacoSchemaModel: sync('app/monaco@schemaModel'),
    monacoUiSchemaModel: sync('app/monaco@uischemaModel'),
    monacoDataModel: sync('app/monaco@dataModel'),
    monacoI18NModel: sync('app/monaco@i18nModel'),
    locale: sync('app/jsonforms@locale'),
    formonly(): boolean {
      return this.$route.query?.view === 'form-only';
    },
  },
  mounted() {
    this.setExample(
      find(this.app.examples, (example) => example.id === this.$route.params.id)
    );
  },
  watch: {
    '$route.params.id'(id) {
      this.setExample(find(this.app.examples, (example) => example.id === id));
    },
  },
  methods: {
    onChange(event: JsonFormsChangeEvent): void {
      const data = event.data;
      // preserve data into the input so that it is saved when we switch to webcomponet and back
      this.$set(this.app.example.input, 'data', data);

      this.$store.set(
        'app/monaco@dataModel',
        getMonacoModelForUri(
          monaco.Uri.parse(this.toDataUri(this.app.example.id)),
          data ? JSON.stringify(data, null, 2) : ''
        )
      );
    },
    onWebComponentChange(customEvent: CustomEvent): void {
      const details = customEvent.detail as any[];
      if (details && details.length > 0) {
        const event: JsonFormsChangeEvent = details[0];
        const data = event.data;

        // preserve data into the input so that it is saved when we switch to webcomponet and back
        this.$set(this.app.example.input, 'data', data);

        this.$store.set(
          'app/monaco@dataModel',
          getMonacoModelForUri(
            monaco.Uri.parse(this.toDataUri(this.app.example.id)),
            data ? JSON.stringify(data, null, 2) : ''
          )
        );
      }
    },
    setExample(example: Example): void {
      if (example) {
        this.app.example = {
          id: example.id,
          title: example.title,
          input: {
            schema: example.input.schema,
            uischema: example.input.uischema,
            data: example.input.data,
            i18n: example.input.i18n,
            renderers: example.input.renderers,
          },
        };
        this.updateMonacoModels(this.app.example);
      }
    },
    reloadMonacoSchema(): void {
      const example = find(
        this.app.examples,
        (example) => example.id === this.$route.params.id
      );

      if (example) {
        this.$store.set(
          'app/monaco@schemaModel',
          getMonacoModelForUri(
            monaco.Uri.parse(this.toSchemaUri(example.id)),
            example.input.schema
              ? JSON.stringify(example.input.schema, null, 2)
              : ''
          )
        );
        this.toast('Original example schema loaded. Apply it to take effect.');
      }
    },
    saveMonacoSchema(): void {
      const model = this.monacoSchemaModel as monaco.editor.ITextModel;
      const example = this.app.example;

      if (model && example) {
        // TODO: is there a better way how to get errors including the error message from monaco editor ?
        const hasError =
          model
            .getAllDecorations()
            .filter((d) => d.options.className === 'squiggly-error')
            .map((e) => e).length > 0;

        const modelValue = model.getValue();
        if (modelValue && !hasError) {
          const newJson: Record<string, any> = JSON.parse(modelValue);

          this.$set(example.input, 'schema', newJson);
          this.toast('New schema applied');
        } else if (hasError) {
          this.toast('Error: schema is invalid');
        }
      }
    },
    reloadMonacoUiSchema(): void {
      const example = find(
        this.app.examples,
        (example) => example.id === this.$route.params.id
      );

      if (example) {
        this.$store.set(
          'app/monaco@uischemaModel',
          getMonacoModelForUri(
            monaco.Uri.parse(this.toUiSchemaUri(example.id)),
            example.input.uischema
              ? JSON.stringify(example.input.uischema, null, 2)
              : ''
          )
        );
        this.toast(
          'Original example UI schema loaded. Apply it to take effect.'
        );
      }
    },
    saveMonacoUiSchema(): void {
      const model = this.monacoUiSchemaModel as monaco.editor.ITextModel;
      const example = this.app.example;

      if (model && example) {
        // TODO: is there a better way how to get errors including the error message from monaco editor ?
        const hasError =
          model
            .getAllDecorations()
            .filter((d) => d.options.className === 'squiggly-error')
            .map((e) => e).length > 0;

        const modelValue = model.getValue();
        if (modelValue && !hasError) {
          const newJson: Record<string, any> = JSON.parse(modelValue);

          this.$set(example.input, 'uischema', newJson);
          this.toast('New UI schema applied');
        } else if (hasError) {
          this.toast('Error: UI schema is invalid');
        }
      }
    },
    reloadMonacoData(): void {
      const example = find(
        this.app.examples,
        (example) => example.id === this.$route.params.id
      );

      if (example) {
        this.$store.set(
          'app/monaco@dataModel',
          getMonacoModelForUri(
            monaco.Uri.parse(this.toDataUri(example.id)),
            example.input.data
              ? JSON.stringify(example.input.data, null, 2)
              : ''
          )
        );
        this.toast('Original example data loaded. Apply it to take effect.');
      }
    },
    saveMonacoData(): void {
      const model = this.monacoDataModel as monaco.editor.ITextModel;
      const example = this.app.example;

      if (model && example) {
        // do not check for monaco errors just if this is valid JSON becase we want to see when we have validation errors

        const modelValue = model.getValue();
        if (modelValue) {
          let newJson: Record<string, any> | undefined = undefined;

          try {
            newJson = JSON.parse(modelValue);
          } catch (error) {
            this.toast(`Error: ${error}`);
          }

          if (newJson) {
            this.$set(example.input, 'data', newJson);
            this.toast('New data applied');
          }
        }
      }
    },
    reloadMonacoI18N(): void {
      const example = find(
        this.app.examples,
        (example) => example.id === this.$route.params.id
      );

      if (example) {
        this.$store.set(
          'app/monaco@i18nModel',
          getMonacoModelForUri(
            monaco.Uri.parse(this.toI18NUri(example.id)),
            example.input.i18n
              ? JSON.stringify(example.input.i18n, null, 2)
              : ''
          )
        );
        this.toast('Original example i18n loaded. Apply it to take effect.');
      }
    },
    saveMonacoI18N(): void {
      const model = this.monacoI18NModel as monaco.editor.ITextModel;
      const example = this.app.example;

      if (model && example) {
        // do not check for monaco errors just if this is valid JSON becase we want to see when we have validation errors

        const modelValue = model.getValue();
        if (modelValue) {
          let newJson: Record<string, any> | undefined = undefined;

          try {
            newJson = JSON.parse(modelValue);
          } catch (error) {
            this.toast(`Error: ${error}`);
          }

          if (newJson) {
            this.$set(example.input, 'i18n', newJson);
            this.toast('New i18n applied');
          }
        }
      }
    },
    registerValidations(editor: EditorApi): Record<string, any> {
      configureJsonSchemaValidation(editor, ['*.schema.json']);
      configureUISchemaValidation(editor, ['*.uischema.json']);
      for (let example of this.app.examples) {
        const schema = {
          ...example.input.schema,
          title: example.title,
        };

        if (example && example.input.schema) {
          configureDataValidation(
            editor,
            `inmemory://${this.toSchemaUri(example.id)}`,
            this.toDataUri(example.id),
            schema
          );
        }
      }

      const options: Record<string, any> = {};
      return options;
    },
    updateMonacoModels(example: Example): void {
      this.$store.set(
        'app/monaco@schemaModel',
        getMonacoModelForUri(
          monaco.Uri.parse(this.toSchemaUri(example.id)),
          example.input.schema
            ? JSON.stringify(example.input.schema, null, 2)
            : ''
        )
      );
      this.$store.set(
        'app/monaco@uischemaModel',
        getMonacoModelForUri(
          monaco.Uri.parse(this.toUiSchemaUri(example.id)),
          example.input.uischema
            ? JSON.stringify(example.input.uischema, null, 2)
            : ''
        )
      );
      this.$store.set(
        'app/monaco@dataModel',
        getMonacoModelForUri(
          monaco.Uri.parse(this.toDataUri(example.id)),
          example.input.data ? JSON.stringify(example.input.data, null, 2) : ''
        )
      );
      this.$store.set(
        'app/monaco@i18nModel',
        getMonacoModelForUri(
          monaco.Uri.parse(this.toI18NUri(example.id)),
          example.input.i18n ? JSON.stringify(example.input.i18n, null, 2) : ''
        )
      );
    },
    toSchemaUri(id: string): string {
      return `${id}.schema.json`;
    },
    toUiSchemaUri(id: string): string {
      return `${id}.uischema.json`;
    },
    toDataUri(id: string): string {
      return `${id}.data.json`;
    },
    toI18NUri(id: string): string {
      return `${id}.i18n.json`;
    },
    toast(message: string): void {
      this.snackbar = true;
      this.snackbarText = message;
    },
  },
  provide() {
    return {
      styles: myStyles,
    };
  },
};
